/**
 * Richtprijzen — overzicht voor het accountteam: het huistarief uit de
 * historie (alle klanten, laatste 12 maanden) als vertrekpunt, en per account
 * de status van de klant-assistent: mag hij prijzen noemen, hoeveel regels
 * staan aan, wie heeft het geverifieerd. Per account bijstellen gebeurt op de
 * klantpagina (tab Richtprijzen).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { authFetch } from '../../utils/authHelpers';
import { useUserRole } from '../../contexts/UserRoleContext';
import { AI_ENGINE_URL } from '../../utils/apiConfig';

const eur = (v) => (v == null ? '—' : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v));
const when = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function Richtprijzen() {
  const navigate = useNavigate();
  const { permissions } = useUserRole();
  const canEdit = !!permissions?.offertes?.edit;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `${AI_ENGINE_URL}/api/v1/price-guidance`;

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${base}/overview`);
      if (!r.ok) throw new Error('Overzicht laden mislukt');
      setData(await r.json());
    } catch (e) { setError(e.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const fillHouse = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${base}/house/fill`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      if (!r.ok) throw new Error('Huistarief vastleggen mislukt');
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!data && !error) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  const house = data?.house || [];
  const accs = (data?.accounts || []).slice().sort((a, b) => Number(b.pricing_enabled) - Number(a.pricing_enabled) || (a.account_name || '').localeCompare(b.account_name || ''));

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>Richtprijzen</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Wat de klant-assistent mag zeggen als een klant vraagt wat iets kost. Het huistarief hieronder is het vertrekpunt uit de offertehistorie;
        per account stelt het accountteam de bandbreedte bij en zet de assistent aan. Zonder die schakelaar noemt hij nooit een bedrag.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>Huistarief — uit de historie</Typography>
            <Typography variant="body2" color="text.secondary">Mediaan en p25–p75 per projecttype van uitgevoerde projecten (geannuleerd, afgewezen en nog-in-offerte uitgesloten), alle klanten, laatste 12 maanden, minimaal 3 projecten. Afgerond op honderdtallen.</Typography>
          </Box>
          {canEdit && <Button size="small" variant="outlined" startIcon={<HistoryIcon />} disabled={busy} onClick={fillHouse}>Vastleggen als basis</Button>}
        </Stack>
        <Table size="small" sx={{ '& td, & th': { fontSize: 13.5 } }}>
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Projecttype</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Mediaan</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Bandbreedte (p25–p75)</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Projecten</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Basis vastgelegd</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {house.map((r) => (
              <TableRow key={r.label}>
                <TableCell sx={{ fontWeight: 600 }}>{r.label}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{eur(r.suggested?.median)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{eur(r.suggested?.min)} – {eur(r.suggested?.max)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.suggested?.n ?? '—'}</TableCell>
                <TableCell>{r.id ? <Chip size="small" color="success" variant="outlined" label={`${eur(r.price_min)} – ${eur(r.price_max)}`} /> : <Typography variant="caption" color="text.disabled">nog niet</Typography>}</TableCell>
              </TableRow>
            ))}
            {house.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Geen projecttypes met minimaal drie projecten in de laatste 12 maanden.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>Per account</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Alleen accounts waar al iets is ingesteld. Nieuwe accounts: open de klantpagina → tab Richtprijzen.</Typography>
        <Table size="small" sx={{ '& td, & th': { fontSize: 13.5 } }}>
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Assistent</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Regels aan</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Geverifieerd door</TableCell>
            <TableCell />
          </TableRow></TableHead>
          <TableBody>
            {accs.map((a) => (
              <TableRow key={a.account_airtable_id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{a.account_name || a.account_airtable_id}</TableCell>
                <TableCell>{a.pricing_enabled ? <Chip size="small" color="success" label="mag prijzen noemen" /> : <Chip size="small" label="uit" />}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{a.enabled_rows} / {a.rows}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{a.verified_by ? `${a.verified_by} · ${when(a.verified_at)}` : '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" endIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate(`/accounts/${a.account_airtable_id}/workspace?tab=richtprijzen`)}>Openen</Button>
                </TableCell>
              </TableRow>
            ))}
            {accs.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Nog geen accounts ingesteld.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
